import { useState, useMemo, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { useAuthorization } from "@/contexts/AuthorizationContext";
import { useIsMobile } from "@/hooks/use-mobile";
import AdicionarLead from "@/components/ClientDetails/AdicionarLead";
import { ClientDetails } from "@/components/ClientDetails";
import { ExtendedLead } from "@/components/ClientDetails/types";
import {
  PRODUTOS_LEAD_ADEQUACOES_REFORMAS,
  PRODUTO_LEAD_ADEQUACOES_REFORMAS,
  PRODUTO_LEAD_ADEQUACOES_REFORMAS_ELETRICAS,
  PRODUTO_LEAD_ADEQUACOES_REFORMAS_FV,
  PRODUTO_LEAD_SISTEMA_ALARME_INCENDIO,
  PRODUTO_LEAD_SISTEMA_DETCCAO_AUTOMATICA_INCENDIO,
  isProdutoLeadAdequacoesReformasFv,
  isProdutoLeadIncendio,
  isProdutoLeadSdai,
  leadProdutoBadgeMeta,
  formatLeadNomeComProduto,
} from "@/constants/leadsProduto";
import {
  ATIVIDADES_PROPOSTA_ELETRICAS,
  ATIVIDADES_PROPOSTA_FV,
  ATIVIDADES_PROPOSTA_INCENDIO_ALARME,
  ATIVIDADES_PROPOSTA_INCENDIO_SDAI,
  atividadesPropostaOrdenadasPorLabel,
  COMPOSICAO_KIT_FOTOVOLTAICO_INICIAL,
  estadoInicialAtividades,
  ID_ATIVIDADE_KIT_FOTOVOLTAICO,
  isIdEquipamentoIncendio,
  MARGEM_PADRAO_ATIVIDADE_ADEQUACOES,
  parseMargemPercentString,
  precoVendaUnitarioAdequacoes,
  resolverComposicaoKitFotovoltaico,
  type AtividadePropostaAdequacoesDef,
  type ComposicaoKitFotovoltaicoForm,
  type EquipamentoKitFotovoltaicoRef,
  type EstadoLinhaAtividadeProposta,
  type KitFotovoltaicoPdf,
} from "@/constants/adequacoesPropostaAtividades";
import {
  COMPOSICAO_SISTEMA_INCENDIO_INICIAL,
  parseQtdIncendio,
  quantidadeEquipamentoIncendio,
  rotuloAtividadeIncendio,
  rotuloTipoCentralIncendio,
  TIPOS_CENTRAL_ALARME_INCENDIO,
  type ComposicaoSistemaIncendioForm,
  type TipoCentralAlarmeIncendio,
} from "@/constants/adequacoesIncendio";
import { formatBrazilianCurrency, formatBrazilianCurrencyValue } from "@/utils/formatters";
import { CarregadoresMoneyInput } from "@/components/CarregadoresMoneyInput";
import ContratoAdequacaoFormulario from "@/components/contratos/ContratoAdequacaoFormulario";
import { anexarContratoPrincipalAdequacao } from "@/utils/contratosAdequacoesUtils";
import KitFotovoltaicoCampos from "@/components/Adequacoes/KitFotovoltaicoCampos";
import { modulosService } from "@/services/modulosService";
import { inversoresService } from "@/services/inversoresService";
import {
  Plus,
  Eye,
  Pencil,
  Loader2,
  LayoutList,
  Wrench,
  Calculator,
  Trash2,
  FileSignature,
  Download,
  FileText,
  Search,
} from "lucide-react";

const STATUS_OPTIONS = [
  { value: "rascunho", label: "Rascunho" },
  { value: "enviada", label: "Enviada" },
  { value: "aceita", label: "Aceita" },
  { value: "recusada", label: "Recusada" },
] as const;

export const STATUS_CONTRATO_ADEQUACAO = [
  { value: "contrato_gerado", label: "Não assinado" },
  { value: "assinado", label: "Assinado" },
  { value: "em_andamento", label: "Em andamento" },
  { value: "finalizado", label: "Finalizado" },
] as const;

const getContratoStatusLabel = (status?: string | null) => {
  const s = STATUS_CONTRATO_ADEQUACAO.find((o) => o.value === status);
  return s?.label || status || "—";
};

type FiltroLinhaAdequacoes = "todos" | "fv" | "eletricas" | "incendio";

type LinhaResumoOrcamentoAdequacoes = {
  id: string;
  label: string;
  isDesconto: boolean;
  quantidade: number;
  custoUnit: number;
  margem: number;
  precoUnit: number;
  custoTotal: number;
  receitaTotal: number;
  lucroTotal: number;
};

type ResumoOrcamentoAdequacoes = {
  linhas: LinhaResumoOrcamentoAdequacoes[];
  custoTotal: number;
  receitaTotal: number;
  lucroTotal: number;
};

type PropostaAdequacaoRow = {
  id: string;
  lead_id: string;
  descricao_servico: string;
  valor_mao_obra: number;
  valor_materiais: number;
  valor_outros: number;
  valor_total: number;
  observacoes: string | null;
  status: string;
  created_at: string | null;
  proposta_pdf_url: string | null;
  pdf_conteudo?: unknown;
  contratos_adequacoes_reformas?: Array<{
    id: string;
    proposta_id: string;
    contrato_pdf_url?: string | null;
    status?: string | null;
    created_at?: string | null;
  }>;
  leads: {
    id: string;
    nome: string | null;
    tipo: string | null;
    razao_social: string | null;
    vendedor_id: string | null;
    parceiro_id: string | null;
    produto: string | null;
    parceiro: { id: string; nome: string | null } | null;
  } | null;
};

export default function AdequacoesReformas() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { userProfile } = useAuthorization();
  const isMobile = useIsMobile();

  const [activeTab, setActiveTab] = useState("leads");
  const [isAddLeadOpen, setIsAddLeadOpen] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isEditingLead, setIsEditingLead] = useState(false);
  const [selectedLead, setSelectedLead] = useState<ExtendedLead | null>(null);
  const [isSubmittingProposta, setIsSubmittingProposta] = useState(false);
  const [pdfGeneratingId, setPdfGeneratingId] = useState<string | null>(null);
  const [filtroLinha, setFiltroLinha] = useState<FiltroLinhaAdequacoes>("todos");
  const [searchQuery, setSearchQuery] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [isContractFormOpen, setIsContractFormOpen] = useState(false);
  const [selectedPropostaForContract, setSelectedPropostaForContract] =
    useState<PropostaAdequacaoRow | null>(null);
  const [excluindoContratoId, setExcluindoContratoId] = useState<string | null>(null);

  const [formLeadId, setFormLeadId] = useState("");
  const [formDescricao, setFormDescricao] = useState("");
  const [formObservacoes, setFormObservacoes] = useState("");
  const [atividadeLinhas, setAtividadeLinhas] = useState<
    Record<string, EstadoLinhaAtividadeProposta>
  >({});
  const [composicaoIncendio, setComposicaoIncendio] =
    useState<ComposicaoSistemaIncendioForm>(COMPOSICAO_SISTEMA_INCENDIO_INICIAL);
  const [composicaoKit, setComposicaoKit] = useState<ComposicaoKitFotovoltaicoForm>(
    COMPOSICAO_KIT_FOTOVOLTAICO_INICIAL
  );

  const isVendedor = userProfile?.role === "vendedor";
  const isParceiro = userProfile?.role === "parceiro";
  const isParceiroPai = isParceiro && userProfile?.pode_criar_rede_parceiros === true;
  const podeGerenciarContratos = userProfile?.role === "admin" || userProfile?.role === "vendedor";
  const isLeadFromRede = (lead: { parceiro_id?: string | null } | null) =>
    !!lead && isParceiroPai && lead.parceiro_id !== userProfile?.id;

  const isPropostaFromRede = (proposta: PropostaAdequacaoRow) => {
    const pid = proposta.leads?.parceiro_id;
    return !!pid && isParceiroPai && pid !== userProfile?.id;
  };

  const getLeadDisplayName = (lead: { tipo?: string; nome?: string | null; razao_social?: string | null }) => {
    if (!lead) return "—";
    if (lead.tipo === "juridica") {
      return lead.razao_social || lead.nome || "Sem nome";
    }
    return lead.nome || "Sem nome";
  };

  const invalidateLeads = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["leads-adequacoes-reformas"] });
  }, [queryClient]);

  const invalidatePropostas = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["propostas-adequacoes-reformas", userProfile?.id] });
  }, [queryClient, userProfile?.id]);

  const { data: leads, isLoading } = useQuery({
    queryKey: ["leads-adequacoes-reformas", userProfile?.id, isParceiroPai, filtroLinha],
    queryFn: async () => {
      const produtosQuery =
        filtroLinha === "fv"
          ? [PRODUTO_LEAD_ADEQUACOES_REFORMAS_FV]
          : filtroLinha === "eletricas"
            ? [PRODUTO_LEAD_ADEQUACOES_REFORMAS_ELETRICAS, PRODUTO_LEAD_ADEQUACOES_REFORMAS]
            : filtroLinha === "incendio"
              ? [PRODUTO_LEAD_SISTEMA_ALARME_INCENDIO, PRODUTO_LEAD_SISTEMA_DETCCAO_AUTOMATICA_INCENDIO]
              : [...PRODUTOS_LEAD_ADEQUACOES_REFORMAS];

      let query = supabase
        .from("leads")
        .select(
          `
          *,
          parceiro:parceiro_id (
            id,
            nome
          )
        `
        )
        .in("produto", produtosQuery)
        .eq("crm_funil", "vendas");

      if (isVendedor) {
        query = query.eq("vendedor_id", userProfile?.id as string);
      } else if (isParceiro && !isParceiroPai) {
        query = query.eq("parceiro_id", userProfile?.id as string);
      }

      const { data, error } = await query.order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!userProfile?.id,
  });

  const { data: propostas, isLoading: isLoadingPropostas } = useQuery({
    queryKey: ["propostas-adequacoes-reformas", userProfile?.id, isParceiroPai],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("propostas_adequacoes_reformas")
        .select(
          `
          id,
          lead_id,
          descricao_servico,
          valor_mao_obra,
          valor_materiais,
          valor_outros,
          valor_total,
          observacoes,
          status,
          created_at,
          proposta_pdf_url,
          pdf_conteudo,
          leads (
            id,
            nome,
            tipo,
            razao_social,
            cnpj,
            cpf,
            representante_legal,
            cpf_representante,
            cep,
            endereco,
            numero,
            cidade,
            estado,
            vendedor_id,
            parceiro_id,
            produto,
            parceiro:parceiro_id ( id, nome )
          )
        `
        )
        .order("created_at", { ascending: false });
      if (error) throw error;

      const rows = (data ?? []) as unknown as PropostaAdequacaoRow[];
      const propostaIds = rows.map((p) => p.id);
      if (propostaIds.length === 0) return rows;

      const { data: contratos, error: contratosError } = await supabase
        .from("contratos_adequacoes_reformas")
        .select("id, proposta_id, contrato_pdf_url, status, created_at")
        .in("proposta_id", propostaIds);

      if (contratosError) {
        console.warn("Erro ao buscar contratos de adequações:", contratosError);
        return rows;
      }

      return rows.map((proposta) =>
        anexarContratoPrincipalAdequacao(proposta, contratos ?? [])
      );
    },
    enabled: !!userProfile?.id,
  });

  const sortedLeads = useMemo(() => {
    const list = [...(leads ?? [])];
    return list.sort((a: { created_at?: string | null }, b: { created_at?: string | null }) => {
      const aDate = new Date(a.created_at || 0).getTime();
      const bDate = new Date(b.created_at || 0).getTime();
      return bDate - aDate;
    });
  }, [leads]);

  const filteredLeads = useMemo(() => {
    if (!appliedSearch) return sortedLeads;
    const q = appliedSearch.toLowerCase();
    return sortedLeads.filter((lead: Record<string, unknown> & { id: string }) => {
      const name = getLeadDisplayName(lead as ExtendedLead).toLowerCase();
      const telefone = String(lead.telefone ?? "");
      const email = String(lead.email ?? "").toLowerCase();
      const produto = String(lead.produto ?? "").toLowerCase();
      const produtoLabel = (leadProdutoBadgeMeta(String(lead.produto ?? ""))?.label || "").toLowerCase();
      const parceiro = String(
        (lead.parceiro as { nome?: string } | null | undefined)?.nome ?? ""
      ).toLowerCase();
      return (
        name.includes(q) ||
        telefone.includes(appliedSearch) ||
        email.includes(q) ||
        produto.includes(q) ||
        produtoLabel.includes(q) ||
        parceiro.includes(q)
      );
    });
  }, [sortedLeads, appliedSearch]);

  const leadsSemProposta = useMemo(() => {
    const ids = new Set((propostas ?? []).map((p) => p.lead_id));
    return sortedLeads.filter((l: { id: string }) => !ids.has(l.id));
  }, [sortedLeads, propostas]);

  const propostasAceitas = useMemo(
    () => (propostas ?? []).filter((p) => p.status === "aceita"),
    [propostas]
  );

  const leadSelecionadoForm = useMemo(() => {
    if (!formLeadId) return null;
    return sortedLeads.find((l: { id: string }) => l.id === formLeadId) as ExtendedLead | undefined;
  }, [sortedLeads, formLeadId]);

  const isLeadIncendio = isProdutoLeadIncendio(leadSelecionadoForm?.produto);
  const isLeadSdai = isProdutoLeadSdai(leadSelecionadoForm?.produto);
  const isLeadFv = isProdutoLeadAdequacoesReformasFv(leadSelecionadoForm?.produto);

  const catalogoAtividadesProposta: readonly AtividadePropostaAdequacoesDef[] = useMemo(() => {
    const p = leadSelecionadoForm?.produto ?? null;
    const catalogo = isProdutoLeadAdequacoesReformasFv(p)
      ? ATIVIDADES_PROPOSTA_FV
      : isProdutoLeadSdai(p)
        ? ATIVIDADES_PROPOSTA_INCENDIO_SDAI
        : isProdutoLeadIncendio(p)
          ? ATIVIDADES_PROPOSTA_INCENDIO_ALARME
          : ATIVIDADES_PROPOSTA_ELETRICAS;
    return atividadesPropostaOrdenadasPorLabel(catalogo);
  }, [leadSelecionadoForm?.produto]);

  const { data: modulosKit = [], isLoading: carregandoModulosKit } = useQuery({
    queryKey: ["adequacoes-modulos-fotovoltaicos"],
    queryFn: () => modulosService.getAll(),
    enabled: isLeadFv,
    staleTime: 5 * 60 * 1000,
  });

  const { data: inversoresKit = [], isLoading: carregandoInversoresKit } = useQuery({
    queryKey: ["adequacoes-inversores"],
    queryFn: () => inversoresService.getAll(),
    enabled: isLeadFv,
    staleTime: 5 * 60 * 1000,
  });

  const modulosKitRef = useMemo<EquipamentoKitFotovoltaicoRef[]>(
    () =>
      modulosKit.map((m) => ({
        id: m.id,
        marca: m.marca,
        modelo: m.modelo,
        potenciaW: m.potencia,
      })),
    [modulosKit]
  );

  const inversoresKitRef = useMemo<EquipamentoKitFotovoltaicoRef[]>(
    () =>
      inversoresKit.map((i) => ({
        id: i.id,
        marca: i.marca,
        modelo: i.modelo,
        potenciaW: i.potenciaNominal,
      })),
    [inversoresKit]
  );

  useEffect(() => {
    if (!formLeadId) {
      setAtividadeLinhas({});
      setComposicaoIncendio(COMPOSICAO_SISTEMA_INCENDIO_INICIAL);
      setComposicaoKit(COMPOSICAO_KIT_FOTOVOLTAICO_INICIAL);
      return;
    }
    setAtividadeLinhas(estadoInicialAtividades(catalogoAtividadesProposta));
    setComposicaoIncendio(COMPOSICAO_SISTEMA_INCENDIO_INICIAL);
    setComposicaoKit(COMPOSICAO_KIT_FOTOVOLTAICO_INICIAL);
  }, [formLeadId, catalogoAtividadesProposta]);

  const parseQtdAtividade = (s: string) => {
    const n = parseInt(String(s).replace(/\D/g, "") || "1", 10);
    if (!Number.isFinite(n) || n < 1) return 1;
    return n;
  };

  const quantidadeLinhaAtividade = (
    def: AtividadePropostaAdequacoesDef,
    st: EstadoLinhaAtividadeProposta
  ) => {
    if (isLeadIncendio && isIdEquipamentoIncendio(def.id)) {
      const qEquip = quantidadeEquipamentoIncendio(def.id, composicaoIncendio);
      if (qEquip != null) return qEquip;
    }
    return parseQtdAtividade(st.quantidade);
  };

  const rotuloLinhaAtividade = (def: AtividadePropostaAdequacoesDef) => {
    if (!isLeadIncendio) return def.label;
    return rotuloAtividadeIncendio(def.id, def.label, composicaoIncendio);
  };

  const descricaoComercialLinha = (def: AtividadePropostaAdequacoesDef) => {
    const base = rotuloLinhaAtividade(def);
    if (def.id !== ID_ATIVIDADE_KIT_FOTOVOLTAICO) return base;
    const composto = resolverComposicaoKitFotovoltaico(composicaoKit, modulosKitRef, inversoresKitRef);
    if (!composto.ok) return base;
    return `${base} — ${composto.resumo}`;
  };

  const resumoOrcamento = useMemo((): ResumoOrcamentoAdequacoes => {
    const linhas: LinhaResumoOrcamentoAdequacoes[] = [];
    let custoTotal = 0;
    let receitaTotal = 0;
    let lucroTotal = 0;

    for (const def of catalogoAtividadesProposta) {
      const st = atividadeLinhas[def.id];
      if (!st?.ativo) continue;
      const q = quantidadeLinhaAtividade(def, st);

      if (def.isDesconto) {
        const va = Number.isFinite(st.valorAbatimento) ? st.valorAbatimento : 0;
        const rec = -Math.abs(va * q);
        linhas.push({
          id: def.id,
          label: descricaoComercialLinha(def),
          isDesconto: true,
          quantidade: q,
          custoUnit: 0,
          margem: 0,
          precoUnit: va > 0 ? -Math.abs(va) : 0,
          custoTotal: 0,
          receitaTotal: rec,
          lucroTotal: rec,
        });
        receitaTotal += rec;
        lucroTotal += rec;
        continue;
      }

      const c = Number.isFinite(st.custo) ? st.custo : 0;
      const mParsed = parseMargemPercentString(st.margem);
      const m = mParsed !== null && mParsed >= 0 ? mParsed : 0;
      const p = precoVendaUnitarioAdequacoes(c, m);
      const ct = c * q;
      const rt = p * q;
      const lt = (p - c) * q;
      linhas.push({
        id: def.id,
        label: descricaoComercialLinha(def),
        isDesconto: false,
        quantidade: q,
        custoUnit: c,
        margem: m,
        precoUnit: p,
        custoTotal: ct,
        receitaTotal: rt,
        lucroTotal: lt,
      });
      custoTotal += ct;
      receitaTotal += rt;
      lucroTotal += lt;
    }

    return { linhas, custoTotal, receitaTotal, lucroTotal };
  }, [
    atividadeLinhas,
    catalogoAtividadesProposta,
    composicaoIncendio,
    composicaoKit,
    inversoresKitRef,
    isLeadIncendio,
    modulosKitRef,
  ]);

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { error } = await supabase
        .from("propostas_adequacoes_reformas")
        .update({
          status,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Status atualizado" });
      invalidatePropostas();
    },
    onError: () => {
      toast({ title: "Erro ao atualizar status", variant: "destructive" });
    },
  });

  const deletePropostaMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("propostas_adequacoes_reformas").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Proposta excluída" });
      invalidatePropostas();
    },
    onError: () => {
      toast({ title: "Erro ao excluir proposta", variant: "destructive" });
    },
  });

  const handleOpenContractForm = (proposta: PropostaAdequacaoRow) => {
    setSelectedPropostaForContract(proposta);
    setIsContractFormOpen(true);
  };

  const updateContratoStatusMutation = useMutation({
    mutationFn: async ({ contratoId, status }: { contratoId: string; status: string }) => {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) throw new Error("Sessão expirada");
      const response = await fetch(`/api/contratos-adequacoes-reformas/${contratoId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || "Falha ao atualizar status");
      }
    },
    onSuccess: () => {
      toast({ title: "Status do contrato atualizado" });
      invalidatePropostas();
    },
    onError: (err: Error) => {
      toast({
        title: "Erro ao atualizar status",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const handleExcluirContrato = async (contratoId: string) => {
    setExcluindoContratoId(contratoId);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) throw new Error("Sessão expirada");
      const response = await fetch(`/api/contratos-adequacoes-reformas/${contratoId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || "Falha ao excluir contrato");
      }
      const result = await response.json().catch(() => ({}));
      toast({
        title: "Contrato removido",
        description: result?.leadRevertido
          ? "O PDF foi excluído e o cliente voltou a ser lead na etapa Análise de Necessidades."
          : "O PDF foi excluído. Os dados do formulário foram preservados para regeneração.",
      });
      invalidatePropostas();
    } catch (err) {
      toast({
        title: "Erro ao excluir contrato",
        description: err instanceof Error ? err.message : "Erro inesperado",
        variant: "destructive",
      });
    } finally {
      setExcluindoContratoId(null);
    }
  };

  const gerarPdfProposta = async (propostaId: string) => {
    setPdfGeneratingId(propostaId);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const response = await fetch("/api/propostas/adequacoes-reformas/gerar", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token ?? ""}`,
        },
        body: JSON.stringify({ propostaId }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(
          typeof err.details === "string" ? err.details : err.error || "Falha ao gerar PDF"
        );
      }
      const data = await response.json();
      toast({ title: "PDF gerado com sucesso" });
      if (data.pdfUrl) window.open(data.pdfUrl, "_blank");
      invalidatePropostas();
    } catch (e: unknown) {
      toast({
        title: "Erro ao gerar PDF",
        description: e instanceof Error ? e.message : undefined,
        variant: "destructive",
      });
    } finally {
      setPdfGeneratingId(null);
    }
  };

  const openDetails = (lead: ExtendedLead, editing: boolean) => {
    setSelectedLead(lead);
    setIsEditingLead(editing);
    setIsDetailsOpen(true);
  };

  const irParaNovaProposta = (leadId: string) => {
    setFormLeadId(leadId);
    setActiveTab("new");
  };

  const temProposta = (leadId: string) => (propostas ?? []).some((p) => p.lead_id === leadId);

  const resetFormProposta = () => {
    setFormLeadId("");
    setFormDescricao("");
    setFormObservacoes("");
    setAtividadeLinhas({});
    setComposicaoIncendio(COMPOSICAO_SISTEMA_INCENDIO_INICIAL);
    setComposicaoKit(COMPOSICAO_KIT_FOTOVOLTAICO_INICIAL);
  };

  const linhaAtividadeVazia = (): EstadoLinhaAtividadeProposta => ({
    ativo: false,
    custo: 0,
    margem: String(MARGEM_PADRAO_ATIVIDADE_ADEQUACOES),
    quantidade: "1",
    valorAbatimento: 0,
  });

  const setLinhaAtividade = (id: string, patch: Partial<EstadoLinhaAtividadeProposta>) => {
    setAtividadeLinhas((prev) => ({
      ...prev,
      [id]: { ...(prev[id] ?? linhaAtividadeVazia()), ...patch },
    }));
  };

  const handleSubmitProposta = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formLeadId) {
      toast({ title: "Selecione um cliente", variant: "destructive" });
      return;
    }

    if (isLeadIncendio) {
      const qCentral = parseQtdIncendio(composicaoIncendio.qtdCentral);
      if (qCentral < 1) {
        toast({
          title: "Composição incompleta",
          description: "Informe a quantidade da central de alarme (mínimo 1).",
          variant: "destructive",
        });
        return;
      }
      if (isLeadSdai && parseQtdIncendio(composicaoIncendio.qtdDetectores) < 1) {
        toast({
          title: "Composição incompleta",
          description: "Informe a quantidade de detectores de fumaça para o SDAI.",
          variant: "destructive",
        });
        return;
      }
    }

    const investimentoLinhas: Array<{
      descricao: string;
      quantidade: string;
      valor_unitario: number;
      valor_total: number;
    }> = [];
    const orcamentoLinhasDetalhe: Array<{
      descricao: string;
      quantidade: number;
      custo_unitario: number;
      margem: number;
      preco_unitario: number;
      custo_total: number;
      receita_total: number;
      lucro_total: number;
    }> = [];
    const escopoRotulos: string[] = [];
    const atividadesEscopoIds: string[] = [];
    let totalReceita = 0;
    let totalCusto = 0;
    let totalLucro = 0;
    let temServicoPositivo = false;
    let kitFotovoltaicoSalvo: KitFotovoltaicoPdf | null = null;

    for (const def of catalogoAtividadesProposta) {
      const st = atividadeLinhas[def.id];
      if (!st?.ativo) continue;
      const q = quantidadeLinhaAtividade(def, st);

      if (def.id === ID_ATIVIDADE_KIT_FOTOVOLTAICO) {
        const composto = resolverComposicaoKitFotovoltaico(
          composicaoKit,
          modulosKitRef,
          inversoresKitRef
        );
        if (!composto.ok) {
          toast({
            title: "Kit fotovoltaico incompleto",
            description: composto.erro,
            variant: "destructive",
          });
          return;
        }
        kitFotovoltaicoSalvo = composto.payload;
      }

      const labelLinha = descricaoComercialLinha(def);

      if (def.isDesconto) {
        const va = Number.isFinite(st.valorAbatimento) ? st.valorAbatimento : 0;
        if (va <= 0 || q < 1) {
          toast({
            title: "Desconto inválido",
            description: "Informe valor positivo do desconto e quantidade.",
            variant: "destructive",
          });
          return;
        }
        const vt = -Math.abs(va * q);
        investimentoLinhas.push({
          descricao: labelLinha,
          quantidade: String(q),
          valor_unitario: -Math.abs(va),
          valor_total: vt,
        });
        orcamentoLinhasDetalhe.push({
          descricao: labelLinha,
          quantidade: q,
          custo_unitario: 0,
          margem: 0,
          preco_unitario: -Math.abs(va),
          custo_total: 0,
          receita_total: vt,
          lucro_total: vt,
        });
        totalReceita += vt;
        totalLucro += vt;
        continue;
      }

      const c = Number.isFinite(st.custo) ? st.custo : 0;
      const mParsed = parseMargemPercentString(st.margem);

      if (c <= 0 || mParsed === null || mParsed < 0 || q < 1) {
        toast({
          title: "Valores incompletos",
          description: `Informe custo, margem (%) ≥ 0 e quantidade válidos para "${labelLinha}".`,
          variant: "destructive",
        });
        return;
      }

      const m = mParsed;
      const p = precoVendaUnitarioAdequacoes(c, m);
      const vt = p * q;
      const ct = c * q;
      const lt = (p - c) * q;

      investimentoLinhas.push({
        descricao: labelLinha,
        quantidade: String(q),
        valor_unitario: p,
        valor_total: vt,
      });
      orcamentoLinhasDetalhe.push({
        descricao: labelLinha,
        quantidade: q,
        custo_unitario: c,
        margem: m,
        preco_unitario: p,
        custo_total: ct,
        receita_total: vt,
        lucro_total: lt,
      });
      totalReceita += vt;
      totalCusto += ct;
      totalLucro += lt;
      temServicoPositivo = true;
      escopoRotulos.push(labelLinha);
      if (
        !def.isDesconto &&
        def.id !== "ele_mao_obra" &&
        def.id !== "fv_mao_obra" &&
        def.id !== "inc_mao_obra"
      ) {
        atividadesEscopoIds.push(def.id);
      }
    }

    if (!temServicoPositivo) {
      toast({
        title: "Inclua ao menos uma atividade",
        description: "Ative um item (exceto apenas desconto) com custo, margem (%) e quantidade.",
        variant: "destructive",
      });
      return;
    }
    if (totalReceita <= 0) {
      toast({
        title: "Total inválido",
        description: "O valor cobrado total deve ser maior que zero após o desconto.",
        variant: "destructive",
      });
      return;
    }

    const descricaoMontada = (() => {
      const partes: string[] = [];
      if (formDescricao.trim()) partes.push(formDescricao.trim());
      if (escopoRotulos.length) {
        partes.push(`Itens previstos: ${escopoRotulos.join("; ")}.`);
      }
      return partes.join("\n\n").trim();
    })();

    setIsSubmittingProposta(true);
    try {
      const { data: userData } = await supabase.auth.getUser();
      const { error } = await supabase.from("propostas_adequacoes_reformas").insert({
        lead_id: formLeadId,
        created_by: userData.user?.id ?? null,
        descricao_servico: descricaoMontada,
        valor_mao_obra: 0,
        valor_materiais: 0,
        valor_outros: totalReceita,
        valor_total: totalReceita,
        observacoes: formObservacoes.trim() || null,
        status: "enviada",
        pdf_conteudo: {
          atividades_escopo_ids: atividadesEscopoIds,
          detalhamento_escopo: formDescricao.trim() || null,
          investimento_linhas: investimentoLinhas,
          orcamento_linhas_detalhe: orcamentoLinhasDetalhe,
          orcamento_totais: {
            custo_total: totalCusto,
            receita_total: totalReceita,
            lucro_total: totalLucro,
          },
          ...(isLeadIncendio
            ? {
                sistema_incendio: {
                  tipo_central: composicaoIncendio.tipoCentral,
                  qtd_central: parseQtdIncendio(composicaoIncendio.qtdCentral),
                  qtd_acionadores: parseQtdIncendio(composicaoIncendio.qtdAcionadores),
                  qtd_sirenes: parseQtdIncendio(composicaoIncendio.qtdSirenes),
                  qtd_avisadores: parseQtdIncendio(composicaoIncendio.qtdAvisadores),
                  qtd_detectores: isLeadSdai
                    ? parseQtdIncendio(composicaoIncendio.qtdDetectores)
                    : 0,
                },
              }
            : {}),
          ...(kitFotovoltaicoSalvo ? { kit_fotovoltaico: kitFotovoltaicoSalvo } : {}),
        },
      });
      if (error) {
        if (error.code === "23505") {
          toast({
            title: "Proposta já existe",
            description: "Este cliente já possui uma proposta. Veja na aba Propostas.",
            variant: "destructive",
          });
          invalidatePropostas();
          return;
        }
        throw error;
      }
      toast({
        title: "Proposta registrada",
        description: "Na aba Propostas use Gerar PDF para baixar o documento no padrão SUN7.",
      });
      resetFormProposta();
      invalidatePropostas();
      setActiveTab("list");
    } catch (err) {
      console.error(err);
      toast({
        title: "Erro ao salvar proposta",
        description: "Confirme se a migração do banco foi aplicada.",
        variant: "destructive",
      });
    } finally {
      setIsSubmittingProposta(false);
    }
  };

  const renderLeadsToolbar = () => (
    <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:gap-4 w-full sm:w-auto flex-1">
        <form
          className={`flex ${isMobile ? "flex-col" : "flex-row"} gap-2 flex-1`}
          onSubmit={(e) => {
            e.preventDefault();
            setAppliedSearch(searchQuery);
          }}
        >
          <Input
            placeholder="Pesquisar Nome ou Razão Social"
            className={isMobile ? "w-full" : "w-64"}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <Button type="submit" variant="outline" className={isMobile ? "w-full" : ""}>
            <Search className="h-4 w-4 mr-2" /> Buscar
          </Button>
        </form>
        <div className="space-y-1 flex-1 min-w-[200px]">
          <Label className="text-xs text-muted-foreground">Filtrar lista</Label>
          <Select value={filtroLinha} onValueChange={(v) => setFiltroLinha(v as FiltroLinhaAdequacoes)}>
            <SelectTrigger>
              <SelectValue placeholder="Linha" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todas as linhas</SelectItem>
              <SelectItem value="fv">Somente FV</SelectItem>
              <SelectItem value="eletricas">Somente elétricas</SelectItem>
              <SelectItem value="incendio">Proteção contra incêndio</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <Dialog open={isAddLeadOpen} onOpenChange={setIsAddLeadOpen}>
        <DialogTrigger asChild>
          <Button className="w-full sm:w-auto shrink-0">
            <Plus className="h-4 w-4 mr-2" />
            Novo cliente
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-[425px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Novo cliente — Adequações e Reformas</DialogTitle>
            <DialogDescription>
              Selecione um ou mais produtos (FV, elétricas, alarme de incêndio e/ou SDAI). Cada produto cria uma
              negociação separada no CRM. O lead entra no funil de vendas.
            </DialogDescription>
          </DialogHeader>
          <AdicionarLead
            onClose={() => setIsAddLeadOpen(false)}
            onSuccess={() => {
              invalidateLeads();
              setIsAddLeadOpen(false);
              toast({ title: "Cliente cadastrado" });
            }}
            defaultProduct={PRODUTO_LEAD_ADEQUACOES_REFORMAS_FV}
            lockProduct
            allowMultipleProducts
            allowDuplicateProducts
            allowedProductOptions={[
              PRODUTO_LEAD_ADEQUACOES_REFORMAS_FV,
              PRODUTO_LEAD_ADEQUACOES_REFORMAS_ELETRICAS,
              PRODUTO_LEAD_SISTEMA_ALARME_INCENDIO,
              PRODUTO_LEAD_SISTEMA_DETCCAO_AUTOMATICA_INCENDIO,
            ]}
          />
        </DialogContent>
      </Dialog>
    </div>
  );

  const renderLeadsBody = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      );
    }
    if (filteredLeads.length === 0) {
      return (
        <p className="text-center text-muted-foreground py-10">
          {appliedSearch
            ? "Nenhum lead encontrado para a busca informada."
            : "Nenhum cliente cadastrado ainda. Use \"Novo cliente\" para começar."}
        </p>
      );
    }
    if (isMobile) {
      return (
        <div className="space-y-3">
          {filteredLeads.map((lead: Record<string, unknown> & { id: string }) => (
            <Card key={lead.id}>
              <CardContent className="p-4 space-y-2 text-sm">
                <div className="flex justify-between items-start gap-2 flex-wrap">
                  <div className="font-semibold text-base">{getLeadDisplayName(lead as ExtendedLead)}</div>
                  <div className="flex flex-wrap gap-1 justify-end">
                    <Badge variant="outline" className="shrink-0 text-xs">
                      {leadProdutoBadgeMeta(String(lead.produto ?? ""))?.label ?? "—"}
                    </Badge>
                    {temProposta(lead.id) ? (
                      <Badge className="shrink-0 bg-green-100 text-green-800 border-green-200">Com proposta</Badge>
                    ) : (
                      <Badge variant="outline" className="shrink-0 text-muted-foreground">
                        Sem proposta
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="text-muted-foreground">
                  {String(lead.telefone ?? "")}
                  {lead.email ? ` · ${lead.email}` : ""}
                </div>
                <div className="text-muted-foreground">
                  {lead.created_at ? new Date(String(lead.created_at)).toLocaleDateString("pt-BR") : "—"}
                </div>
                <div className="flex flex-col gap-2 pt-2">
                  <Button variant="outline" size="sm" onClick={() => openDetails(lead as ExtendedLead, false)}>
                    <Eye className="h-4 w-4 mr-2" />
                    Ver ficha
                  </Button>
                  {!isLeadFromRede(lead as { parceiro_id?: string | null }) && (
                    <Button variant="outline" size="sm" onClick={() => openDetails(lead as ExtendedLead, true)}>
                      <Pencil className="h-4 w-4 mr-2" />
                      Editar
                    </Button>
                  )}
                  {!temProposta(lead.id) && !isLeadFromRede(lead as { parceiro_id?: string | null }) && (
                    <Button variant="default" size="sm" onClick={() => irParaNovaProposta(lead.id)}>
                      <Calculator className="h-4 w-4 mr-2" />
                      Gerar proposta
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      );
    }
    return (
      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Cliente</TableHead>
              <TableHead>Linha</TableHead>
              <TableHead>Telefone</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Proposta</TableHead>
              <TableHead>Cadastro</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredLeads.map((lead: Record<string, unknown> & { id: string }) => (
              <TableRow key={lead.id}>
                <TableCell className="font-medium">{getLeadDisplayName(lead as ExtendedLead)}</TableCell>
                <TableCell>
                  <Badge variant="outline" className="text-xs font-normal whitespace-nowrap">
                    {leadProdutoBadgeMeta(String(lead.produto ?? ""))?.label ?? "—"}
                  </Badge>
                </TableCell>
                <TableCell>{String(lead.telefone ?? "—")}</TableCell>
                <TableCell className="max-w-[200px] truncate">{String(lead.email ?? "—")}</TableCell>
                <TableCell>
                  {temProposta(lead.id) ? (
                    <Badge className="bg-green-100 text-green-800 border-green-200">Sim</Badge>
                  ) : (
                    <Badge variant="outline" className="text-muted-foreground">Não</Badge>
                  )}
                </TableCell>
                <TableCell>
                  {lead.created_at ? new Date(String(lead.created_at)).toLocaleDateString("pt-BR") : "—"}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end flex-wrap gap-2">
                    <Button variant="outline" size="sm" onClick={() => openDetails(lead as ExtendedLead, false)}>
                      <Eye className="h-4 w-4 mr-1" />
                      Ver
                    </Button>
                    {!isLeadFromRede(lead as { parceiro_id?: string | null }) && (
                      <Button variant="outline" size="sm" onClick={() => openDetails(lead as ExtendedLead, true)}>
                        <Pencil className="h-4 w-4 mr-1" />
                        Editar
                      </Button>
                    )}
                    {!temProposta(lead.id) && !isLeadFromRede(lead as { parceiro_id?: string | null }) && (
                      <Button variant="default" size="sm" onClick={() => irParaNovaProposta(lead.id)}>
                        <Calculator className="h-4 w-4 mr-1" />
                        Proposta
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground flex items-center gap-2">
            <Wrench className="h-8 w-8 text-primary shrink-0" aria-hidden />
            Adequações e Reformas
          </h1>
          <p className="text-muted-foreground mt-1 max-w-3xl">
            Gestão de oportunidades, propostas comerciais e contratos nesta linha — fluxo alinhado à página de
            Carregadores Veiculares.
          </p>
        </div>
        <Button variant="outline" className="shrink-0 w-full sm:w-auto" onClick={() => navigate("/pipeline")}>
          <LayoutList className="h-4 w-4 mr-2" />
          Abrir CRM (funil)
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="flex w-full flex-wrap justify-start gap-1 h-auto min-h-[2.5rem]">
          <TabsTrigger value="leads" className="flex-1 min-w-[calc(50%-0.25rem)] sm:min-w-0 sm:flex-initial">
            Leads Cadastrados
          </TabsTrigger>
          <TabsTrigger value="new" className="flex-1 min-w-[calc(50%-0.25rem)] sm:min-w-0 sm:flex-initial">
            Gerar Nova Proposta
          </TabsTrigger>
          <TabsTrigger value="list" className="flex-1 min-w-[calc(50%-0.25rem)] sm:min-w-0 sm:flex-initial">
            Propostas
          </TabsTrigger>
          <TabsTrigger value="contratos" className="flex-1 min-w-[calc(50%-0.25rem)] sm:min-w-0 sm:flex-initial">
            Contratos
          </TabsTrigger>
        </TabsList>

        <TabsContent value="leads" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Clientes nesta linha</CardTitle>
              <CardDescription>
                Leads com produto {PRODUTO_LEAD_ADEQUACOES_REFORMAS_FV}, {PRODUTO_LEAD_ADEQUACOES_REFORMAS_ELETRICAS},{" "}
                {PRODUTO_LEAD_SISTEMA_ALARME_INCENDIO}, {PRODUTO_LEAD_SISTEMA_DETCCAO_AUTOMATICA_INCENDIO} ou legado
                &quot;{PRODUTO_LEAD_ADEQUACOES_REFORMAS}&quot;. Detalhe o escopo nas observações do cadastro ou na
                proposta.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {renderLeadsToolbar()}
              {renderLeadsBody()}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="new" className="mt-6">
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Nova proposta</CardTitle>
                <CardDescription>
                  Selecione o cliente, ative as atividades da proposta com o interruptor e informe custo, margem e
                  quantidade. Em alarme e SDAI, preencha também a composição do sistema. No Kit Fotovoltaico (reformas
                  FV), informe modelo e quantidade de placas e de inversores. Uma proposta por cliente.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmitProposta} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Cliente (lead)</Label>
                    <Select value={formLeadId} onValueChange={setFormLeadId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione um cliente sem proposta" />
                      </SelectTrigger>
                      <SelectContent>
                        {leadsSemProposta.map((lead: { id: string; produto?: string | null } & Record<string, unknown>) => (
                          <SelectItem key={lead.id} value={lead.id}>
                            {formatLeadNomeComProduto(lead as ExtendedLead)}
                          </SelectItem>
                        ))}
                        {leadsSemProposta.length === 0 && (
                          <div className="p-2 text-sm text-muted-foreground text-center">
                            Nenhum lead disponível sem proposta
                          </div>
                        )}
                      </SelectContent>
                    </Select>
                    {formLeadId && leadSelecionadoForm && (
                      <p className="text-xs text-muted-foreground">
                        Catálogo de itens:{" "}
                        {isProdutoLeadAdequacoesReformasFv(leadSelecionadoForm.produto)
                          ? PRODUTO_LEAD_ADEQUACOES_REFORMAS_FV
                          : isLeadIncendio
                            ? String(leadSelecionadoForm.produto)
                            : `${PRODUTO_LEAD_ADEQUACOES_REFORMAS_ELETRICAS} ou legado “${PRODUTO_LEAD_ADEQUACOES_REFORMAS}”`}
                        .
                      </p>
                    )}
                  </div>
                  {formLeadId ? (
                    <div className="space-y-4">
                      {isLeadIncendio ? (
                        <div className="space-y-3 rounded-lg border p-3">
                          <div className="space-y-1">
                            <Label>Composição do sistema</Label>
                            <p className="text-sm text-muted-foreground">
                              Informe o tipo da central e os quantitativos dos dispositivos. Esses dados constam no
                              PDF da proposta.
                            </p>
                          </div>
                          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div className="space-y-2">
                              <Label htmlFor="incendio-tipo-central">Tipo da central de alarme</Label>
                              <Select
                                value={composicaoIncendio.tipoCentral}
                                onValueChange={(v) =>
                                  setComposicaoIncendio((p) => ({
                                    ...p,
                                    tipoCentral: v as TipoCentralAlarmeIncendio,
                                  }))
                                }
                              >
                                <SelectTrigger id="incendio-tipo-central">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {TIPOS_CENTRAL_ALARME_INCENDIO.map((opt) => (
                                    <SelectItem key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="incendio-qtd-central">Quantidade de centrais</Label>
                              <Input
                                id="incendio-qtd-central"
                                type="number"
                                min={1}
                                step={1}
                                value={composicaoIncendio.qtdCentral}
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  if (raw === "") {
                                    setComposicaoIncendio((p) => ({ ...p, qtdCentral: "" }));
                                    return;
                                  }
                                  if (!/^\d+$/.test(raw)) return;
                                  setComposicaoIncendio((p) => ({ ...p, qtdCentral: raw }));
                                }}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="incendio-qtd-acionadores">Acionadores manuais</Label>
                              <Input
                                id="incendio-qtd-acionadores"
                                type="number"
                                min={0}
                                step={1}
                                value={composicaoIncendio.qtdAcionadores}
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  if (raw === "") {
                                    setComposicaoIncendio((p) => ({ ...p, qtdAcionadores: "" }));
                                    return;
                                  }
                                  if (!/^\d+$/.test(raw)) return;
                                  setComposicaoIncendio((p) => ({ ...p, qtdAcionadores: raw }));
                                }}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="incendio-qtd-sirenes">Sirenes</Label>
                              <Input
                                id="incendio-qtd-sirenes"
                                type="number"
                                min={0}
                                step={1}
                                value={composicaoIncendio.qtdSirenes}
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  if (raw === "") {
                                    setComposicaoIncendio((p) => ({ ...p, qtdSirenes: "" }));
                                    return;
                                  }
                                  if (!/^\d+$/.test(raw)) return;
                                  setComposicaoIncendio((p) => ({ ...p, qtdSirenes: raw }));
                                }}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="incendio-qtd-avisadores">Avisadores visuais</Label>
                              <Input
                                id="incendio-qtd-avisadores"
                                type="number"
                                min={0}
                                step={1}
                                value={composicaoIncendio.qtdAvisadores}
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  if (raw === "") {
                                    setComposicaoIncendio((p) => ({ ...p, qtdAvisadores: "" }));
                                    return;
                                  }
                                  if (!/^\d+$/.test(raw)) return;
                                  setComposicaoIncendio((p) => ({ ...p, qtdAvisadores: raw }));
                                }}
                              />
                            </div>
                            {isLeadSdai ? (
                              <div className="space-y-2">
                                <Label htmlFor="incendio-qtd-detectores">Detectores de fumaça</Label>
                                <Input
                                  id="incendio-qtd-detectores"
                                  type="number"
                                  min={1}
                                  step={1}
                                  value={composicaoIncendio.qtdDetectores}
                                  onChange={(e) => {
                                    const raw = e.target.value;
                                    if (raw === "") {
                                      setComposicaoIncendio((p) => ({ ...p, qtdDetectores: "" }));
                                      return;
                                    }
                                    if (!/^\d+$/.test(raw)) return;
                                    setComposicaoIncendio((p) => ({ ...p, qtdDetectores: raw }));
                                  }}
                                />
                              </div>
                            ) : null}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Central selecionada: {rotuloTipoCentralIncendio(composicaoIncendio.tipoCentral)}.
                            {isLeadSdai ? " Detectores de fumaça são obrigatórios no SDAI." : ""}
                          </p>
                        </div>
                      ) : null}
                      <div className="space-y-2">
                        <Label>Atividades da proposta</Label>
                        <p className="text-sm text-muted-foreground">
                          Cada item começa desligado — informe custo e margem em % sobre o custo. O preço unitário é
                          calculado automaticamente (custo × (1 + margem ÷ 100)).
                          {isLeadFv
                            ? " No Kit Fotovoltaico, informe também o modelo e a quantidade de placas e de inversores. Cabos CC estão inclusos."
                            : isLeadIncendio
                              ? " A quantidade dos dispositivos segue a composição do sistema."
                              : " Ajuste a quantidade conforme o escopo."}
                        </p>
                      </div>
                      <div className="space-y-4">
                        {catalogoAtividadesProposta.map((def) => {
                          const st = atividadeLinhas[def.id] ?? {
                            ativo: false,
                            custo: 0,
                            margem: def.isDesconto ? "0" : String(MARGEM_PADRAO_ATIVIDADE_ADEQUACOES),
                            quantidade: "1",
                            valorAbatimento: 0,
                          };
                          const switchId = `adequacao-atividade-${def.id}`;
                          const custoN = Number.isFinite(st.custo) ? st.custo : 0;
                          const margemParsed = parseMargemPercentString(st.margem);
                          const margemNum =
                            margemParsed !== null && margemParsed >= 0 ? margemParsed : 0;
                          const precoUnit = def.isDesconto
                            ? 0
                            : precoVendaUnitarioAdequacoes(custoN, margemNum);
                          return (
                            <div key={def.id}>
                              <div className="flex items-center justify-between gap-4 rounded-lg border p-3">
                                <div className="space-y-0.5 min-w-0">
                                  <Label htmlFor={switchId} className="text-base">
                                    {rotuloLinhaAtividade(def)}
                                  </Label>
                                  {def.isDesconto ? (
                                    <p className="text-sm text-muted-foreground">
                                      Valor positivo; será abatido da receita (valor × quantidade).
                                    </p>
                                  ) : def.hint ? (
                                    <p className="text-sm text-muted-foreground">{def.hint}</p>
                                  ) : null}
                                </div>
                                <Switch
                                  id={switchId}
                                  checked={st.ativo}
                                  onCheckedChange={(checked) =>
                                    setLinhaAtividade(def.id, { ativo: checked })
                                  }
                                />
                              </div>
                              {st.ativo && def.id === ID_ATIVIDADE_KIT_FOTOVOLTAICO ? (
                                <KitFotovoltaicoCampos
                                  value={composicaoKit}
                                  onChange={(patch) =>
                                    setComposicaoKit((prev) => ({ ...prev, ...patch }))
                                  }
                                  modulos={modulosKit}
                                  inversores={inversoresKit}
                                  carregando={carregandoModulosKit || carregandoInversoresKit}
                                />
                              ) : null}
                              {st.ativo ? (
                                def.isDesconto ? (
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <CarregadoresMoneyInput
                                      id={`${def.id}-abatimento`}
                                      label="Valor do desconto (R$)"
                                      value={st.valorAbatimento}
                                      onValueChange={(n) =>
                                        setLinhaAtividade(def.id, { valorAbatimento: n })
                                      }
                                    />
                                    <div className="space-y-2">
                                      <Label htmlFor={`${def.id}-qtd`}>Quantidade</Label>
                                      <Input
                                        id={`${def.id}-qtd`}
                                        type="number"
                                        min={1}
                                        step={1}
                                        value={st.quantidade}
                                        onChange={(e) => {
                                          const raw = e.target.value;
                                          if (raw === "") {
                                            setLinhaAtividade(def.id, { quantidade: "" });
                                            return;
                                          }
                                          if (!/^\d+$/.test(raw)) return;
                                          const n = parseInt(raw, 10);
                                          if (n < 1) return;
                                          setLinhaAtividade(def.id, { quantidade: raw });
                                        }}
                                      />
                                    </div>
                                  </div>
                                ) : (
                                  <div
                                    className={`grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4${
                                      def.id === ID_ATIVIDADE_KIT_FOTOVOLTAICO ? " mt-3" : ""
                                    }`}
                                  >
                                    <CarregadoresMoneyInput
                                      id={`${def.id}-custo`}
                                      label="Custo (R$)"
                                      value={st.custo}
                                      onValueChange={(n) => setLinhaAtividade(def.id, { custo: n })}
                                    />
                                    <div className="space-y-2">
                                      <Label htmlFor={`${def.id}-margem`}>Margem (%)</Label>
                                      <Input
                                        id={`${def.id}-margem`}
                                        type="number"
                                        inputMode="decimal"
                                        step="0.01"
                                        min={0}
                                        value={st.margem}
                                        onChange={(e) => {
                                          const raw = e.target.value;
                                          if (raw === "") {
                                            setLinhaAtividade(def.id, { margem: "" });
                                            return;
                                          }
                                          if (!/^[\d]*[,.]?\d*$/.test(raw)) return;
                                          setLinhaAtividade(def.id, { margem: raw });
                                        }}
                                      />
                                    </div>
                                    <div className="space-y-2">
                                      <Label htmlFor={`${def.id}-qtd`}>
                                        {def.id === ID_ATIVIDADE_KIT_FOTOVOLTAICO
                                          ? "Quantidade de kits"
                                          : "Quantidade"}
                                      </Label>
                                      {isLeadIncendio && isIdEquipamentoIncendio(def.id) ? (
                                        <Input
                                          id={`${def.id}-qtd`}
                                          readOnly
                                          tabIndex={-1}
                                          className="bg-muted tabular-nums"
                                          value={String(quantidadeLinhaAtividade(def, st))}
                                        />
                                      ) : (
                                        <Input
                                          id={`${def.id}-qtd`}
                                          type="number"
                                          min={1}
                                          step={1}
                                          value={st.quantidade}
                                          onChange={(e) => {
                                            const raw = e.target.value;
                                            if (raw === "") {
                                              setLinhaAtividade(def.id, { quantidade: "" });
                                              return;
                                            }
                                            if (!/^\d+$/.test(raw)) return;
                                            const n = parseInt(raw, 10);
                                            if (n < 1) return;
                                            setLinhaAtividade(def.id, { quantidade: raw });
                                          }}
                                        />
                                      )}
                                    </div>
                                    <div className="space-y-2">
                                      <Label htmlFor={`${def.id}-preco`}>Preço (R$)</Label>
                                      <Input
                                        id={`${def.id}-preco`}
                                        readOnly
                                        tabIndex={-1}
                                        className="bg-muted tabular-nums"
                                        value={
                                          custoN <= 0 ? "" : formatBrazilianCurrencyValue(precoUnit)
                                        }
                                        placeholder="0,00"
                                      />
                                    </div>
                                  </div>
                                )
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground border rounded-md p-3 bg-muted/30">
                      Escolha um cliente para carregar o catálogo de atividades (FV, elétricas ou proteção contra incêndio).
                    </p>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="adequacao-desc">Detalhamento do escopo (opcional)</Label>
                    <Textarea
                      id="adequacao-desc"
                      rows={3}
                      placeholder="Observações técnicas ou comerciais complementares…"
                      value={formDescricao}
                      onChange={(e) => setFormDescricao(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      Exibido no PDF da proposta, no item 5, após a tabela de valores.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="adequacao-obs">Observações internas (opcional)</Label>
                    <Input
                      id="adequacao-obs"
                      value={formObservacoes}
                      onChange={(e) => setFormObservacoes(e.target.value)}
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={isSubmittingProposta}>
                    {isSubmittingProposta ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Calculator className="mr-2 h-4 w-4" />
                    )}
                    Salvar proposta
                  </Button>
                </form>
              </CardContent>
            </Card>
            <Card className="bg-primary/5 border-primary/20">
              <CardHeader>
                <CardTitle>Resumo</CardTitle>
                <CardDescription>
                  Por item: custo estimado, valor cobrado e lucro esperado. Totais consolidados abaixo.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {resumoOrcamento.linhas.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    Ative ao menos uma atividade com custo e margem (%) para ver o resumo do orçamento.
                  </p>
                ) : (
                  <>
                    <div className="space-y-3 rounded-md border bg-background p-3">
                      {resumoOrcamento.linhas.map((ln) => (
                        <div
                          key={ln.id}
                          className="space-y-2 border-b border-border/60 pb-3 last:border-0 last:pb-0 text-sm"
                        >
                          <div className="font-medium text-foreground">
                            {ln.label}
                            {ln.quantidade > 1 ? (
                              <span className="text-muted-foreground font-normal">
                                {" "}
                                · qtd. {ln.quantidade}
                              </span>
                            ) : null}
                          </div>
                          {ln.isDesconto ? (
                            <div className="text-xs text-muted-foreground">
                              Efeito na receita / lucro:{" "}
                              <span className="font-semibold text-foreground tabular-nums">
                                {formatBrazilianCurrency(ln.receitaTotal)}
                              </span>
                            </div>
                          ) : (
                            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 text-xs">
                              <div className="text-muted-foreground">
                                Custo estimado (item)
                                <div className="text-sm font-semibold text-foreground tabular-nums mt-0.5">
                                  {formatBrazilianCurrency(ln.custoTotal)}
                                </div>
                              </div>
                              <div className="text-muted-foreground">
                                Valor cobrado (item)
                                <div className="text-sm font-semibold text-foreground tabular-nums mt-0.5">
                                  {formatBrazilianCurrency(ln.receitaTotal)}
                                </div>
                              </div>
                              <div className="text-muted-foreground">
                                Lucro esperado (item)
                                <div className="text-sm font-semibold text-foreground tabular-nums mt-0.5">
                                  {formatBrazilianCurrency(ln.lucroTotal)}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="space-y-2 rounded-md border border-primary/20 bg-primary/5 p-3 text-sm">
                      <div className="flex justify-between gap-2">
                        <span className="text-muted-foreground">Custo total estimado</span>
                        <span className="font-semibold tabular-nums">
                          {formatBrazilianCurrency(resumoOrcamento.custoTotal)}
                        </span>
                      </div>
                      <div className="flex justify-between gap-2">
                        <span className="text-muted-foreground">Valor total cobrado</span>
                        <span className="font-semibold tabular-nums">
                          {formatBrazilianCurrency(resumoOrcamento.receitaTotal)}
                        </span>
                      </div>
                      <div className="flex justify-between gap-2">
                        <span className="text-muted-foreground">Lucro total esperado</span>
                        <span className="font-semibold tabular-nums">
                          {formatBrazilianCurrency(resumoOrcamento.lucroTotal)}
                        </span>
                      </div>
                    </div>
                    <div className="flex justify-between text-lg font-semibold border-t pt-3">
                      <span>Total da proposta (cliente)</span>
                      <span className="text-primary tabular-nums">
                        {formatBrazilianCurrency(resumoOrcamento.receitaTotal)}
                      </span>
                    </div>
                  </>
                )}
                <ul className="text-xs space-y-2 text-muted-foreground border rounded-md bg-background p-4">
                  <li>1. Salve a proposta para aparecer na aba Propostas.</li>
                  <li>
                    2. Gere o PDF comercial — o conteúdo padrão segue o produto do lead (FV, reforma elétrica ou
                    proteção contra incêndio).
                  </li>
                  <li>3. Atualize o status (ex.: aceita) e acompanhe na aba Contratos.</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="list" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Propostas</CardTitle>
              <CardDescription>Listagem e status das propostas de adequação e reformas.</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingPropostas ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : !propostas?.length ? (
                <p className="text-center text-muted-foreground py-10">Nenhuma proposta cadastrada.</p>
              ) : isMobile ? (
                <div className="space-y-3">
                  {propostas.map((p) => (
                    <Card key={p.id}>
                      <CardContent className="p-4 space-y-3 text-sm">
                        <div className="font-semibold">{getLeadDisplayName(p.leads as ExtendedLead)}</div>
                        <p className="text-muted-foreground line-clamp-3">{p.descricao_servico}</p>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Total</span>
                          <span className="font-medium">{formatBrazilianCurrency(Number(p.valor_total))}</span>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Status</Label>
                          <Select
                            value={p.status}
                            disabled={isPropostaFromRede(p)}
                            onValueChange={(v) => updateStatusMutation.mutate({ id: p.id, status: v })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {STATUS_OPTIONS.map((o) => (
                                <SelectItem key={o.value} value={o.value}>
                                  {o.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex flex-col gap-2 pt-1">
                          {!isPropostaFromRede(p) && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="w-full"
                              disabled={pdfGeneratingId === p.id}
                              onClick={() => gerarPdfProposta(p.id)}
                            >
                              {pdfGeneratingId === p.id ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              ) : (
                                <Download className="h-4 w-4 mr-2" />
                              )}
                              {p.proposta_pdf_url ? "Atualizar PDF" : "Gerar PDF"}
                            </Button>
                          )}
                          {p.proposta_pdf_url && (
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              className="w-full"
                              onClick={() => window.open(p.proposta_pdf_url!, "_blank")}
                            >
                              <Download className="h-4 w-4 mr-2" />
                              Abrir PDF
                            </Button>
                          )}
                        </div>
                        {!isPropostaFromRede(p) && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="outline" size="sm" className="w-full text-destructive">
                                <Trash2 className="h-4 w-4 mr-2" />
                                Excluir
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Excluir proposta?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Esta ação não pode ser desfeita.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                  onClick={() => deletePropostaMutation.mutate(p.id)}
                                >
                                  Excluir
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="rounded-md border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Cliente</TableHead>
                        <TableHead className="max-w-[240px]">Escopo</TableHead>
                        <TableHead>Total</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>PDF</TableHead>
                        <TableHead>Criada em</TableHead>
                        <TableHead className="text-right">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {propostas.map((p) => (
                        <TableRow key={p.id}>
                          <TableCell className="font-medium whitespace-nowrap">
                            {getLeadDisplayName(p.leads as ExtendedLead)}
                          </TableCell>
                          <TableCell className="max-w-[280px] text-sm text-muted-foreground align-top">
                            <span className="line-clamp-2">{p.descricao_servico}</span>
                          </TableCell>
                          <TableCell>{formatBrazilianCurrency(Number(p.valor_total))}</TableCell>
                          <TableCell>
                            <Select
                              value={p.status}
                              disabled={isPropostaFromRede(p)}
                              onValueChange={(v) => updateStatusMutation.mutate({ id: p.id, status: v })}
                            >
                              <SelectTrigger className="w-[140px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {STATUS_OPTIONS.map((o) => (
                                  <SelectItem key={o.value} value={o.value}>
                                    {o.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="whitespace-nowrap">
                            <div className="flex flex-wrap items-center gap-1">
                              {!isPropostaFromRede(p) && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  title={p.proposta_pdf_url ? "Atualizar PDF" : "Gerar PDF"}
                                  disabled={pdfGeneratingId === p.id}
                                  onClick={() => gerarPdfProposta(p.id)}
                                >
                                  {pdfGeneratingId === p.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Download className="h-4 w-4" />
                                  )}
                                </Button>
                              )}
                              {p.proposta_pdf_url && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  title="Abrir PDF"
                                  onClick={() => window.open(p.proposta_pdf_url!, "_blank")}
                                >
                                  <FileText className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-sm">
                            {p.created_at ? new Date(p.created_at).toLocaleDateString("pt-BR") : "—"}
                          </TableCell>
                          <TableCell className="text-right">
                            {!isPropostaFromRede(p) && (
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="ghost" size="icon" className="text-destructive">
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Excluir proposta?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Esta ação não pode ser desfeita.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                    <AlertDialogAction
                                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                      onClick={() => deletePropostaMutation.mutate(p.id)}
                                    >
                                      Excluir
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="contratos" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:justify-between">
                <div>
                  <CardTitle>Contratos</CardTitle>
                  <CardDescription>
                    Propostas aceitas — gere o contrato PDF com o escopo e valores da proposta (mesmo fluxo de
                    Carregadores).
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {isLoadingPropostas ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : propostasAceitas.length === 0 ? (
                <p className="text-center text-muted-foreground py-10">
                  Nenhuma proposta aceita ainda. Quando o status for &quot;Aceita&quot;, o registro aparece aqui para
                  geração do contrato.
                </p>
              ) : (
                <div className="grid gap-4 md:grid-cols-2">
                  {propostasAceitas.map((p) => {
                    const contrato = p.contratos_adequacoes_reformas?.[0];
                    const temContrato = Boolean(contrato?.contrato_pdf_url);
                    const daRede = isPropostaFromRede(p);
                    const produtoMeta = leadProdutoBadgeMeta(p.leads?.produto ?? null);

                    return (
                      <Card key={p.id} className="border-dashed">
                        <CardHeader className="pb-2">
                          <div className="flex items-start justify-between gap-2">
                            <CardTitle className="text-base flex items-center gap-2">
                              <FileSignature className="h-4 w-4 text-primary" />
                              {getLeadDisplayName(p.leads as ExtendedLead)}
                            </CardTitle>
                            {temContrato ? (
                              <Badge className="bg-green-100 text-green-800 border-green-200 shrink-0">
                                {getContratoStatusLabel(contrato?.status)}
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="shrink-0">
                                Pendente
                              </Badge>
                            )}
                          </div>
                          <CardDescription className="space-y-1">
                            <span className="block">
                              Total {formatBrazilianCurrency(Number(p.valor_total))}
                              {p.created_at ? ` · ${new Date(p.created_at).toLocaleDateString("pt-BR")}` : ""}
                            </span>
                            {produtoMeta ? (
                              <Badge variant="outline" className={`text-xs ${produtoMeta.badgeClassName}`}>
                                {produtoMeta.label}
                              </Badge>
                            ) : null}
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-2">
                          <p className="text-sm text-muted-foreground line-clamp-3">{p.descricao_servico}</p>
                          {p.proposta_pdf_url ? (
                            <Button
                              type="button"
                              variant="outline"
                              className="w-full"
                              onClick={() => window.open(p.proposta_pdf_url!, "_blank")}
                            >
                              <Download className="h-4 w-4 mr-2" />
                              Abrir proposta (PDF)
                            </Button>
                          ) : null}

                          {temContrato && contrato && podeGerenciarContratos && !daRede ? (
                            <div className="space-y-1.5">
                              <Label className="text-xs text-muted-foreground">Contrato assinado?</Label>
                              <Select
                                value={contrato.status || "contrato_gerado"}
                                onValueChange={(v) =>
                                  updateContratoStatusMutation.mutate({
                                    contratoId: contrato.id,
                                    status: v,
                                  })
                                }
                                disabled={updateContratoStatusMutation.isPending}
                              >
                                <SelectTrigger className="w-full">
                                  <SelectValue placeholder="Status" />
                                </SelectTrigger>
                                <SelectContent>
                                  {STATUS_CONTRATO_ADEQUACAO.map((s) => (
                                    <SelectItem key={s.value} value={s.value}>
                                      {s.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          ) : null}

                          {temContrato && contrato?.contrato_pdf_url ? (
                            <div className="flex flex-col gap-2">
                              <Button
                                type="button"
                                className="w-full"
                                onClick={() => window.open(contrato.contrato_pdf_url!, "_blank")}
                              >
                                <FileText className="h-4 w-4 mr-2" />
                                Ver contrato
                              </Button>
                              {podeGerenciarContratos && !daRede ? (
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button
                                      type="button"
                                      variant="destructive"
                                      className="w-full"
                                      disabled={excluindoContratoId === contrato.id}
                                    >
                                      {excluindoContratoId === contrato.id ? (
                                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                      ) : (
                                        <Trash2 className="h-4 w-4 mr-2" />
                                      )}
                                      Excluir contrato
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Excluir contrato?</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        O PDF será removido e será possível gerar um novo. Os dados cadastrais
                                        preenchidos no formulário serão preservados.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                      <AlertDialogAction onClick={() => handleExcluirContrato(contrato.id)}>
                                        Excluir
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              ) : null}
                            </div>
                          ) : podeGerenciarContratos && !daRede ? (
                            <Button
                              type="button"
                              className="w-full"
                              onClick={() => handleOpenContractForm(p)}
                            >
                              <FileSignature className="h-4 w-4 mr-2" />
                              Gerar contrato
                            </Button>
                          ) : (
                            <Button type="button" variant="secondary" className="w-full" disabled>
                              Aguardando geração do contrato
                            </Button>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={isContractFormOpen} onOpenChange={setIsContractFormOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Gerar contrato — Adequações e Reformas</DialogTitle>
            <DialogDescription>
              Preencha os dados das partes e condições de pagamento. Se já houve geração anterior, os dados
              cadastrais são recuperados automaticamente.
            </DialogDescription>
          </DialogHeader>
          {selectedPropostaForContract ? (
            <ContratoAdequacaoFormulario
              proposta={selectedPropostaForContract}
              onCancel={() => setIsContractFormOpen(false)}
              onSuccess={async (pdfUrl) => {
                setIsContractFormOpen(false);
                setSelectedPropostaForContract(null);
                await invalidatePropostas();
                toast({ title: "Contrato gerado com sucesso!" });
                window.open(pdfUrl, "_blank");
                setActiveTab("contratos");
              }}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedLead && (
            <ClientDetails
              cliente={selectedLead}
              onClose={() => setIsDetailsOpen(false)}
              onUpdate={() => {
                invalidateLeads();
              }}
              defaultIsEditing={isLeadFromRede(selectedLead) ? false : isEditingLead}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
